import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CurrentLang } from 'utils/lang';
import { paymentFailureHandler, paymentPollingHandler } from '../../actions/payment';
import { setStripeSubmitting, stripePaymentAttempt } from '../../actions/stripe';
import stripeEngine from '../../../payments/core/engines/stripe';
import useWhitelabelEnvs from '../whitelabel/useWhitelabelEnvs';

/**
 * useStripe hook.
 *
 * Orchestrates the Stripe PaymentElement lifecycle and the deferred
 * ConfirmationToken -> POST /payments -> handleNextAction flow.
 *
 * @param {React.RefObject} stripeElementRef - Ref to the DOM element where PaymentElement mounts.
 * @returns {{ready: boolean}} Stripe readiness state.
 */
function useStripe(stripeElementRef) {
  const dispatch = useDispatch();
  const requestedPayment = useRef(false);
  const mountedConfig = useRef({ amount: null, currency: null, locale: null });
  const [ready, setReady] = useState(false);

  const env = useWhitelabelEnvs();
  const stripeAppearance = env?.stripe?.appearance;
  const currency = env?.currency;
  const purchaseToken = useSelector((state) => state.purchase.get('token'));
  const purchaseTotal = useSelector((state) => state.purchase.get('total'));
  const selectedPaymentOption = useSelector((state) => state.purchase.get('selectedPaymentOption'));
  const selectedPaymentEngine = useSelector(
    (state) =>
      state.purchase.get('selectedPaymentMethod')?.engine || state.purchase.get('paymentEngine'),
  );
  // The purchaser name/email/phone live in the redux-form `purchaser` form
  // (purchaserFirstName/purchaserLastName/email/phone), NOT in purchase state — the same source
  // the rest of the funnel reads (e.g. useMercadoPagoTDCDynamicValues).
  const purchaser = useSelector((state) => state.form?.purchaser?.values || {});
  const billingAddress = useSelector((state) => state.form?.card?.values?.billingAddress);
  const isUpdating = useSelector((state) => state.purchase.get('isUpdating'));
  const fetchingPayment = useSelector((state) => state.payment.get('isFetching'));
  const stripeLocale = CurrentLang();

  const shouldMountStripe =
    selectedPaymentOption === 'credit_card' && selectedPaymentEngine === 'stripe';

  const resetStripeState = useCallback((shouldResetReady = true) => {
    stripeEngine.unmountPaymentElement();
    mountedConfig.current = { amount: null, currency: null, locale: null };
    requestedPayment.current = false;
    if (shouldResetReady) setReady(false);
  }, []);

  useEffect(() => {
    if (shouldMountStripe) return undefined;

    resetStripeState();
    return undefined;
  }, [resetStripeState, shouldMountStripe]);

  useEffect(() => {
    if (!ready || !shouldMountStripe) return;
    if (mountedConfig.current.locale === stripeLocale) return;

    resetStripeState();
  }, [ready, resetStripeState, shouldMountStripe, stripeLocale]);

  useEffect(() => {
    if (!shouldMountStripe || ready) return undefined;
    if (!stripeElementRef.current || !purchaseToken || purchaseTotal == null || !currency)
      return undefined;

    let cancelled = false;

    /**
     * Mounts Stripe PaymentElement once the SDK is ready.
     */
    const mount = async () => {
      const stripe = await stripeEngine.waitForStripe();
      if (!stripe || cancelled) return;

      const paymentElement = stripeEngine.mountPaymentElement(stripeElementRef.current, {
        amount: purchaseTotal,
        currency,
        appearance: stripeAppearance || {},
        locale: stripeLocale,
      });
      if (!paymentElement || cancelled) return;

      mountedConfig.current = { amount: purchaseTotal, currency, locale: stripeLocale };
      setReady(true);
    };

    mount();

    return () => {
      cancelled = true;
    };
  }, [
    currency,
    purchaseToken,
    purchaseTotal,
    ready,
    shouldMountStripe,
    stripeAppearance,
    stripeElementRef,
    stripeLocale,
  ]);

  useEffect(() => {
    if (!ready || !shouldMountStripe || purchaseTotal == null || !currency) return;

    const amountChanged = mountedConfig.current.amount !== purchaseTotal;
    const currencyChanged = mountedConfig.current.currency !== currency;

    if (!amountChanged && !currencyChanged) return;

    stripeEngine.updateAmount(purchaseTotal, currency);
    mountedConfig.current = { ...mountedConfig.current, amount: purchaseTotal, currency };
  }, [currency, purchaseTotal, ready, shouldMountStripe]);

  useEffect(() => {
    return () => {
      resetStripeState(false);
    };
  }, [resetStripeState]);

  const createStripeError = useCallback((error) => {
    return {
      paymentEngine: 'stripe',
      errorReason: error?.code || 'default',
      message: error?.message || 'Stripe payment error',
    };
  }, []);

  const getPurchaserName = useCallback(() => {
    return [purchaser?.purchaserFirstName, purchaser?.purchaserLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  }, [purchaser?.purchaserFirstName, purchaser?.purchaserLastName]);

  const buildBillingDetails = useCallback(() => {
    const billingDetails = {};
    const purchaserName = getPurchaserName();

    if (purchaserName) billingDetails.name = purchaserName;
    if (purchaser?.email) billingDetails.email = purchaser.email;
    if (purchaser?.phone) billingDetails.phone = purchaser.phone;
    if (billingAddress) billingDetails.address = { line1: billingAddress };

    return billingDetails;
  }, [billingAddress, getPurchaserName, purchaser?.email, purchaser?.phone]);

  /**
   * Submits the Stripe payment.
   * Called externally from Checkout.js via stripeEngine.submit().
   */
  const submit = useCallback(async () => {
    if (requestedPayment.current || isUpdating || fetchingPayment) return;
    if (!purchaseToken) return;

    requestedPayment.current = true;
    // Dedicated loading flag for the client-side Stripe steps (submitElements +
    // createConfirmationToken). It only feeds Checkout's buttonLoading; unlike
    // isUpdating/fetchingPayment it does NOT unmount the Payment Element nor hide the button, so
    // the element stays alive until the ConfirmationToken exists. Cleared in the finally on every
    // exit path; from then on isUpdating/fetchingPayment (set by stripePaymentAttempt) take over.
    dispatch(setStripeSubmitting(true));

    try {
      const submitResult = await stripeEngine.submitElements();
      if (submitResult?.error) {
        paymentFailureHandler(dispatch, createStripeError(submitResult.error));
        return;
      }

      const billingDetails = buildBillingDetails();
      const confirmationResult = await stripeEngine.createConfirmationToken({
        payment_method_data: {
          billing_details: billingDetails,
        },
      });

      if (confirmationResult?.error || !confirmationResult?.confirmationToken?.id) {
        paymentFailureHandler(
          dispatch,
          createStripeError(
            confirmationResult?.error || { message: 'Stripe confirmation token was not created' },
          ),
        );
        return;
      }

      const purchaserName = getPurchaserName();
      const { id: confirmationTokenId, payment_method_preview: paymentMethodPreview } =
        confirmationResult.confirmationToken;
      const card = paymentMethodPreview?.card;

      if (!card) {
        paymentFailureHandler(
          dispatch,
          createStripeError({ message: 'Stripe confirmation token is missing card details' }),
        );
        return;
      }

      const { brand, exp_month: expMonth, exp_year: expYear, funding, last4, country } = card;

      const cardValues = {
        billingAddress,
        card_holder_name: purchaserName,
        card_brand: brand,
        card_exp_month: expMonth,
        card_exp_year: expYear,
        card_type: funding,
        ...(last4 !== undefined && { card_number: `XXXX XXXX XXXX ${last4}` }),
        ...(country !== undefined && { card_country: country }),
      };

      const paymentResult = await dispatch(
        stripePaymentAttempt(purchaseToken, {
          confirmationTokenId,
          cardValues,
        }),
      );

      if (!paymentResult) return;

      if (paymentResult.outcome === 'failed') return;

      if (paymentResult.outcome === 'requires_client_action') {
        const nextActionResult = await stripeEngine.handleNextAction(
          paymentResult.clientAction?.clientSecret,
        );
        if (nextActionResult?.error) {
          paymentFailureHandler(dispatch, createStripeError(nextActionResult.error));
        } else {
          paymentPollingHandler(dispatch, purchaseToken, paymentResult.raw);
        }
      }
    } finally {
      // Always release the re-entrancy guard. It only protects the async submit() window
      // (before stripePaymentAttempt sets isUpdating/fetchingPayment); from here those flags
      // own the button state, so there is no path that should leave the lock stuck.
      requestedPayment.current = false;
      dispatch(setStripeSubmitting(false));
    }
  }, [
    billingAddress,
    buildBillingDetails,
    createStripeError,
    dispatch,
    fetchingPayment,
    getPurchaserName,
    isUpdating,
    purchaseToken,
  ]);

  // Register submit handler with the engine so Checkout.js can trigger it
  useEffect(() => {
    stripeEngine.setSubmitHandler(submit);
    return () => {
      stripeEngine.setSubmitHandler(null);
    };
  }, [submit]);

  return { ready };
}

export default useStripe;
